import { useQueryClient } from '@tanstack/react-query';
import { Link, Outlet, useNavigate } from '@tanstack/react-router';
import {
  BadgeCheck,
  Boxes,
  FileText,
  FlaskConical,
  FolderTree,
  GitMerge,
  Globe,
  LayoutDashboard,
  ListChecks,
  LogOut,
  Tags,
  TicketPercent,
  Truck,
  ArrowLeftRight,
  ClipboardList,
  PackageCheck,
  Calculator,
  ChartColumn,
  Megaphone,
  MessageSquare,
  PackageOpen,
  Percent,
  Plug,
  ReceiptText,
  Scale,
  ScrollText,
  Search,
  Settings,
  Sparkles,
  ShieldCheck,
  ShoppingCart,
  Star,
  Users,
  UsersRound,
  Undo2,
  WandSparkles,
} from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { UI_LANGUAGES, type UiLanguage, setUiLanguage } from '../i18n';
import { api, setCsrfToken } from '../lib/api';
import { can, useMe } from '../lib/session';
import { CommandPalette } from './command-palette';
import { Select } from './ui/input';

interface NavItem {
  to: string;
  label: string;
  icon: ReactNode;
  permission?: string;
}

export function Layout() {
  const { t, i18n } = useTranslation();
  const me = useMe();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const groups: { title?: string; items: NavItem[] }[] = [
    { items: [{ to: '/', label: t('nav.dashboard'), icon: <LayoutDashboard /> }] },
    {
      title: t('nav.catalog'),
      items: [
        { to: '/products', label: t('nav.products'), icon: <Boxes />, permission: 'catalog.read' },
        {
          to: '/categories',
          label: t('nav.categories'),
          icon: <FolderTree />,
          permission: 'catalog.read',
        },
        { to: '/brands', label: t('nav.brands'), icon: <Tags />, permission: 'catalog.read' },
        {
          to: '/attributes',
          label: t('nav.attributes'),
          icon: <ListChecks />,
          permission: 'catalog.read',
        },
        { to: '/search', label: t('nav.search'), icon: <Search />, permission: 'search.manage' },
        { to: '/seo', label: t('nav.seo'), icon: <Globe />, permission: 'seo.manage' },
        {
          to: '/content',
          label: t('nav.content'),
          icon: <FileText />,
          permission: 'content.manage',
        },
      ],
    },
    {
      title: t('nav.sales'),
      items: [
        {
          to: '/orders',
          label: t('nav.orders'),
          icon: <ShoppingCart />,
          permission: 'orders.read',
        },
        {
          to: '/customers',
          label: t('nav.customers'),
          icon: <Users />,
          permission: 'customers.read',
        },
        {
          to: '/segments',
          label: t('nav.segments'),
          icon: <UsersRound />,
          permission: 'customers.read',
        },
        {
          to: '/experiments',
          label: t('nav.experiments'),
          icon: <FlaskConical />,
          permission: 'analytics.read',
        },
        {
          to: '/reviews',
          label: t('nav.reviews'),
          icon: <Star />,
          permission: 'reviews.moderate',
        },
        {
          to: '/promotions',
          label: t('nav.promotions'),
          icon: <TicketPercent />,
          permission: 'promotions.manage',
        },
        {
          to: '/analytics',
          label: t('nav.analytics'),
          icon: <ChartColumn />,
          permission: 'analytics.read',
        },
        {
          to: '/ads',
          label: t('nav.ads'),
          icon: <Megaphone />,
          permission: 'analytics.read',
        },
        { to: '/returns', label: t('nav.returns'), icon: <Undo2 />, permission: 'returns.read' },
        {
          to: '/invoices',
          label: t('nav.invoices'),
          icon: <ReceiptText />,
          permission: 'invoices.read',
        },
        {
          to: '/accounting',
          label: t('nav.accounting'),
          icon: <Calculator />,
          permission: 'invoices.export',
        },
      ],
    },
    {
      title: t('nav.warehouse'),
      items: [
        {
          to: '/warehouse/stock',
          label: t('nav.stock'),
          icon: <Boxes />,
          permission: 'inventory.read',
        },
        {
          to: '/warehouse/inbound',
          label: t('nav.inbound'),
          icon: <PackageOpen />,
          permission: 'inventory.read',
        },
        {
          to: '/warehouse/fulfillment',
          label: t('nav.fulfillment'),
          icon: <PackageCheck />,
          permission: 'fulfillment.ship',
        },
        {
          to: '/warehouse/documents',
          label: t('nav.stockDocuments'),
          icon: <ArrowLeftRight />,
          permission: 'inventory.read',
        },
      ],
    },
    {
      title: t('nav.supply'),
      items: [
        {
          to: '/suppliers',
          label: t('nav.suppliers'),
          icon: <Truck />,
          permission: 'suppliers.read',
        },
        {
          to: '/purchase-orders',
          label: t('nav.purchaseOrders'),
          icon: <ClipboardList />,
          permission: 'suppliers.read',
        },
        {
          to: '/match-queue',
          label: t('nav.matchQueue'),
          icon: <GitMerge />,
          permission: 'imports.run',
        },
      ],
    },
    {
      title: t('nav.pricing'),
      items: [
        {
          to: '/pricing',
          label: t('nav.pricingRules'),
          icon: <Percent />,
          permission: 'pricing.read',
        },
        {
          to: '/price-approvals',
          label: t('nav.priceApprovals'),
          icon: <BadgeCheck />,
          permission: 'pricing.approve',
        },
      ],
    },
  ];

  groups.push({
    title: t('nav.admin'),
    items: [
      {
        to: '/sourcing-rules',
        label: t('nav.sourcingRules'),
        icon: <Scale />,
        permission: 'sourcing.manage',
      },
      { to: '/ai', label: t('nav.ai'), icon: <Sparkles />, permission: 'settings.manage' },
      {
        to: '/ai/drafts',
        label: t('nav.aiDrafts'),
        icon: <WandSparkles />,
        permission: 'ai.content_review',
      },
      {
        to: '/assistant',
        label: t('nav.assistant'),
        icon: <MessageSquare />,
        permission: 'ai.assistant',
      },
      {
        to: '/settings',
        label: t('nav.settings'),
        icon: <Settings />,
        permission: 'settings.manage',
      },
      { to: '/staff', label: t('nav.staff'), icon: <ShieldCheck />, permission: 'users.manage' },
      { to: '/audit', label: t('nav.audit'), icon: <ScrollText />, permission: 'audit.read' },
      {
        to: '/integrations',
        label: t('nav.integrations'),
        icon: <Plug />,
        permission: 'integrations.read',
      },
    ],
  });

  const logout = async () => {
    try {
      await api('/auth/logout', { method: 'POST' });
    } finally {
      setCsrfToken(undefined);
      queryClient.clear();
      await navigate({ to: '/login' });
    }
  };

  return (
    <div className="flex min-h-screen">
      <aside className="flex w-60 shrink-0 flex-col border-r bg-muted/30">
        <div className="px-4 py-4 font-semibold">Allumo Admin</div>
        <div className="px-2 pb-3">
          <CommandPalette />
        </div>
        <nav className="flex-1 space-y-4 px-2" aria-label="Main">
          {groups.map((group, i) => {
            const items = group.items.filter(
              (item) => !item.permission || can(me, item.permission),
            );
            if (items.length === 0) return null;
            return (
              <div key={group.title ?? i}>
                {group.title ? (
                  <p className="px-2 pb-1 text-xs font-medium uppercase text-muted-foreground">
                    {group.title}
                  </p>
                ) : null}
                <ul className="space-y-0.5">
                  {items.map((item) => (
                    <li key={item.to}>
                      <Link
                        to={item.to}
                        activeOptions={{ exact: item.to === '/' }}
                        className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-accent [&.active]:bg-accent [&.active]:font-medium [&_svg]:size-4"
                      >
                        {item.icon}
                        {item.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </nav>
        <div className="space-y-2 border-t p-3 text-sm">
          <p className="truncate text-muted-foreground" title={me.email}>
            {me.displayName ?? me.email}
          </p>
          <Select
            aria-label={t('nav.interfaceLanguage')}
            value={i18n.language}
            onChange={(e) => {
              setUiLanguage(e.target.value as UiLanguage);
            }}
          >
            {UI_LANGUAGES.map((lang) => (
              <option key={lang} value={lang}>
                {lang.toUpperCase()}
              </option>
            ))}
          </Select>
          <button
            type="button"
            onClick={() => void logout()}
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground"
          >
            <LogOut className="size-4" />
            {t('nav.logout')}
          </button>
        </div>
      </aside>
      <main className="min-w-0 flex-1 p-6">
        <Outlet />
      </main>
    </div>
  );
}

export function PageHeader({
  title,
  description,
  actions,
}: {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
        {description ? <p className="mt-1 text-sm text-muted-foreground">{description}</p> : null}
      </div>
      {actions ? <div className="flex gap-2">{actions}</div> : null}
    </div>
  );
}
